import { useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type { HookCreated, HookOut, PageHookOut, SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { formatRelative } from "../lib/format.ts";
import { spaceApi } from "../lib/paths.ts";
import { useAction, useResource } from "../lib/resource.ts";
import type { PageProps } from "../lib/state.ts";
import { Button } from "../ui/Button.tsx";
import { ConfirmButton, Dialog, DialogActions } from "../ui/Dialog.tsx";
import { Field, Form, Input } from "../ui/form.tsx";
import { PlusIcon } from "../ui/icons.tsx";
import { Async, EmptyState } from "../ui/misc.tsx";
import { Table, Td, Th } from "../ui/Table.tsx";
import { SettingsNav } from "./settings/nav.tsx";
import { Secret } from "./settings/secret.tsx";

function HookRow({ space, h, onChange }: { space: SpaceOut; h: HookOut; onChange: () => void }) {
  const [remove, busy] = useAction(async () => {
    await api.del(spaceApi(space, `/hooks/${h.id}`));
    onChange();
  });
  return (
    <tr>
      <Td class="break-all">{h.url}</Td>
      <Td label={t("hooks.events")}>{h.events.join(", ")}</Td>
      <Td label={t("hooks.created")}>{formatRelative(h.created_at)}</Td>
      <Td align="right" label="">
        <ConfirmButton
          size="sm"
          variant="danger"
          label={t("common.delete")}
          title={t("hooks.deleteTitle")}
          message={t("hooks.deleteMessage", { url: h.url })}
          confirmLabel={t("common.delete")}
          busy={busy}
          onConfirm={remove}
        />
      </Td>
    </tr>
  );
}

/** A new webhook: where to, and for which events (a dialog's). */
function CreateHook({
  space,
  onCreated,
  onCancel,
}: {
  space: SpaceOut;
  onCreated: (secret: string) => void;
  onCancel: () => void;
}) {
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState("*");
  const [create, busy] = useAction(async () => {
    const list = events
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);
    const hook = await api.post<HookCreated>(spaceApi(space, "/hooks"), { url, events: list });
    onCreated(hook.secret);
  });
  return (
    <Form class="flex flex-col gap-4" onSubmit={create}>
      <Field label={t("hooks.url")}>
        <Input type="url" value={url} onValue={setUrl} required={true} placeholder="https://" autofocus={true} />
      </Field>
      <Field label={t("hooks.events")} hint={t("hooks.eventsHint")}>
        <Input value={events} onValue={setEvents} required={true} />
      </Field>
      <DialogActions>
        <Button answer="cancel" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" submit={true} busy={busy}>
          {t("hooks.create")}
        </Button>
      </DialogActions>
    </Form>
  );
}

export function Webhooks({ space }: PageProps) {
  const hooks = useResource(`hooks:${space.id}`, (signal) =>
    api.get<PageHookOut>(spaceApi(space, "/hooks"), undefined, signal),
  );
  const [secret, setSecret] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  return (
    <>
      <SettingsNav space={space} tab="/hooks" />
      <div class="mb-4 flex">
        <Button variant="primary" onClick={() => setCreating(true)}>
          <PlusIcon />
          {t("hooks.new")}
        </Button>
      </div>
      <Async r={hooks}>
        {(page) =>
          page.data.length === 0 ? (
            <EmptyState title={t("hooks.empty")} />
          ) : (
            <Table stack={true}>
              <thead class="bg-zinc-50">
                <tr>
                  <Th>{t("hooks.url")}</Th>
                  <Th>{t("hooks.events")}</Th>
                  <Th>{t("hooks.created")}</Th>
                  <Th />
                </tr>
              </thead>
              <tbody class="divide-y divide-zinc-100">
                {page.data.map((h) => (
                  <HookRow key={h.id} space={space} h={h} onChange={hooks.reload} />
                ))}
              </tbody>
            </Table>
          )
        }
      </Async>
      <Dialog open={creating} onClose={() => setCreating(false)} title={t("hooks.create")}>
        <CreateHook
          space={space}
          onCancel={() => setCreating(false)}
          onCreated={(created) => {
            setCreating(false);
            setSecret(created);
            hooks.reload();
          }}
        />
      </Dialog>
      <Secret secret={secret} onClose={() => setSecret(null)} />
    </>
  );
}
